<template>
  <div class="section__content">
    <div class="groups-toolbar">
      <input
        v-model="searchQuery"
        type="text"
        class="groups-search"
        placeholder="Search groups..."
      />

      <div class="sort-controls">
        <label>Sort by:</label>
        <select v-model="sortKey">
          <option value="name">Group Name</option>
          <option value="owner">Owner</option>
          <option value="themes">Total Themes</option>
          <option value="last_modified_on">Last Modified</option>
        </select>
        <div
          style="
            width: 1.5rem;
            height: 1.5rem;
            border: 1px solid var(--outline-colour);
            background: var(--secondary-ui-colour);
            border-radius: 4px;
          "
        >
          <button
            class="tooltip__hoverable icon-only-btn center-align-parent"
            style="margin: 0"
            :aria-labelledby="`sortable-ctm-groups-label`"
            :type="'button'"
            @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'"
          >
            <SvgIcon
              class="center-align-child"
              :aria-hidden="true"
              :focusable="false"
              :icon="sortDirection === 'asc' ? 'ascending' : 'descending'"
              style="margin-left: -0.25rem; margin-top: -0.25rem"
            />
          </button>
        </div>
      </div>
    </div>

    <DefaultTable
      style="width: 100%"
      caption="Topic Model Groups"
      :column-headers="columnHeaders"
      :hide-caption="true"
      :row-data="filteredAndSortedGroups"
    >
      <template #row="{ row, index }">
        <td :data-label="columnHeaders[0]">
          <router-link
            class="datasets__table-name"
            :to="{ name: 'customTopicModelEdit', params: { id: row.id } }"
          >
            {{ row.name }}
          </router-link>
          <span class="custom-text-model__text--light custom-text-model__text--block">
            Tags: {{ row.tags.join(',') || 'none' }}
          </span>
        </td>
        <td :data-label="columnHeaders[1]">
          <span class="custom-text-model__text--bold" v-if="row.created_by.organization">
            Team:
          </span>
          <span class="custom-text-model__text--bold">
            {{ row.created_by.name }}
          </span>
          <span v-if="profile && row.created_by.name == profile.fullname">
            <SvgIconMeaningful icon="star" title="this is you" />
          </span>
        </td>
        <td :data-label="columnHeaders[2]">
          {{ row.themes.length }}
        </td>
        <td :data-label="columnHeaders[3]">
          <a :href="row.source.url" v-if="row.source.url && row.source.name">
            {{ row.source.name }}
          </a>
          <div v-if="!row.source.url && row.source.name">
            {{ row.source.name }}
          </div>
        </td>
        <td :data-label="columnHeaders[4]">
          <div v-if="ACCESS_TYPE_TO_DESC[row.access_type]">
            <VisibleText class="custom-text-model__text--bold">
              {{ ACCESS_TYPE_TO_DESC[row.access_type].name }}
            </VisibleText>
            <VisibleText class="custom-text-model__text--light custom-text-model__text--block">
              {{ ACCESS_TYPE_TO_DESC[row.access_type].desc }}
            </VisibleText>
          </div>
        </td>
        <td :data-label="columnHeaders[5]">
          <VisibleText> {{ LANG_NAME[row.lang] }} </VisibleText>
        </td>
        <td :data-label="columnHeaders[6]">
          {{ new Date(row.last_modified_on.$date).toLocaleString() }}
        </td>

        <td :data-label="columnHeaders[7]">
          <ButtonIconOnly
            icon="deleteIcon"
            :id="`delete-${row.id}`"
            v-if="row.owner == currUserId"
            @click-handler="onClickDelete(row.id, index)"
          >
            <VisibleText>delete</VisibleText>
          </ButtonIconOnly>
        </td>
      </template>
    </DefaultTable>
  </div>
</template>

<script>
// Components
import ButtonIconOnly from '@/components/UI/Button/ButtonIconOnly.vue';
import DefaultTable from '@/components/UI/DefaultTable.vue';
import SvgIconMeaningful from '@/components/UI/Svg/SvgIconMeaningful.vue';
import SvgIcon from '@/components/UI/Svg/SvgIcon.vue';

// Mixins
import ConfirmMixin from '@/utils/mixins/confirmMixin.js';
import UserMixin from '@/utils/mixins/userMixin';

// Services
import customTopicModelService from '@/services/customTopicModelService.js';
import { fetchGroups } from '../Utils/fetch.js';

// Consts
import { LANG_NAME, ACCESS_TYPE_TO_DESC } from '../Utils/consts.js';

export default {
  name: 'CustomTopicModelGroups',
  mixins: [ConfirmMixin, UserMixin],
  components: {
    ButtonIconOnly,
    DefaultTable,
    SvgIconMeaningful,
    SvgIcon,
  },
  data() {
    return {
      CUSTOM_TOPIC_MODEL_SERVICE: new customTopicModelService(this.$pigeonline),
      LANG_NAME: LANG_NAME,
      ACCESS_TYPE_TO_DESC: ACCESS_TYPE_TO_DESC,
      columnHeaders: [
        'Group Name',
        'Owner',
        'Themes',
        'Source',
        'Access Level',
        'Language',
        'Last Modified',
        '',
      ],
      topicModelGroups: [],
      searchQuery: '',
      sortKey: 'last_modified_on',
      sortDirection: 'des',
    };
  },
  async created() {
    this.topicModelGroups = await fetchGroups();
  },
  computed: {
    currUserId() {
      return this.$store.getters['user/getProfile']
        ? this.$store.getters['user/getProfile'].django_ref_id
        : -1;
    },

    filteredAndSortedGroups() {
      let list = [...this.topicModelGroups];

      // --- SEARCH ---
      if (this.searchQuery.trim()) {
        const q = this.searchQuery.toLowerCase();
        list = list.filter(
          (g) =>
            g.name.toLowerCase().includes(q) ||
            (g.tags || []).some((t) => t.toLowerCase().includes(q)),
        );
      }

      // --- SORT ---
      list.sort((a, b) => {
        let A, B;

        switch (this.sortKey) {
          case 'themes':
            A = a.themes.length;
            B = b.themes.length;
            break;
          case 'owner':
            A = a.created_by?.name || '';
            B = b.created_by?.name || '';
            break;
          case 'last_modified_on':
            A = new Date(a.last_modified_on.$date);
            B = new Date(b.last_modified_on.$date);
            break;
          default:
            A = a.name.toLowerCase();
            B = b.name.toLowerCase();
        }

        if (A < B) return this.sortDirection === 'asc' ? -1 : 1;
        if (A > B) return this.sortDirection === 'asc' ? 1 : -1;
        return 0;
      });

      return list;
    },
  },
  methods: {
    onClickDelete(id, index) {
      this.setConfirmText({
        btn: 'delete group',
        title: 'delete group?',
      });
      this.setConfirmSourceComponent('topicModelGroups');
      this.setConfirmStatus(false);
      this.setConfirmTarget({ id: id, index: index });
      this.setConfirmType('delete');
      this.setConfirmIsVisible(true);
    },
    async deleteGroup(id) {
      try {
        // Zombies are deleted in the django_projects as of 1.11.0
        // delete topic models in this group
        // await Promise.all(
        //   this.topicModelGroups[index].themes.map(
        //     async (modelID) => await this.CUSTOM_TOPIC_MODEL_SERVICE.delete(modelID),
        //   ),
        // );
        await this.$pigeonline.projects.delete(id);
        this.topicModelGroups = await fetchGroups();
      } catch (e) {
        throw new Error('CustomTopicModelGroups.vue:deletegroup ' + e.message);
      }
    },
  },
  watch: {
    confirmStatus: async function (val) {
      if (
        val &&
        this.confirmSourceComponent === 'topicModelGroups' &&
        this.confirmType === 'delete'
      ) {
        await this.deleteGroup(this.confirmTarget.id, this.confirmTarget.index);
      }
    },
    $route() {
      if (this.$route.path && this.$route.path == '/custom-text-model') {
        this.topicModelGroups = fetchGroups();
      }
    },
  },
};
</script>

<style scoped>
.groups-toolbar {
  display: flex;
  justify-content: space-between;
  margin-bottom: 1rem;
  gap: 1rem;
}

.groups-search {
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--outline-colour);
  border-radius: 6px;
}

.sort-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.sort-controls select {
  padding: 0.4rem;
  border-radius: 6px;
  border: 1px solid var(--outline-colour);
}
</style>
